import {type QueryClient} from '@tanstack/react-query'
import {useEffect} from 'react'

type UseCancelInactiveQueriesOptions = {
  disabledRoutes?: string[] | RegExp[]
}

export const useCancelInactiveQueries = (
  queryClient: QueryClient,
  options?: UseCancelInactiveQueriesOptions
) => {
  const {disabledRoutes = []} = options || {}

  useEffect(() => {
    return () => {
      // Get current path, use provided path or fallback to window.location
      const path = window.location.pathname

      // Check if current route should be excluded from query cancellation
      const shouldSkipCancellation = disabledRoutes.some((route) => {
        if (typeof route === 'string') {
          return path === route || path.startsWith(route)
        }
        if (route instanceof RegExp) {
          return route.test(path)
        }
        return false
      })

      // Skip cancellation if current route is in disabled routes
      if (shouldSkipCancellation) {
        return
      }

      queryClient
        .getQueryCache()
        .getAll()
        .forEach((query) => {
          /**
           * 1. We cancel inactive queries when the user navigates to a new page.
           * 2. We check all the queries if there are observers attached and if not we can cancel the call.
           *    A query becomes inactive when it has no observers, which basically means:
           *    all components that use it via useQuery have unmounted.
           */
          if (query.queryKey && query.getObserversCount() === 0) {
            queryClient.cancelQueries({queryKey: query.queryKey}).catch((error) => {
              console.error('Error canceling query:', error)
            })
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href, disabledRoutes])
}
